import React, { useContext } from "react";
import CaseStudyBanner from "../DataComponents/CaseStudyBanner/CaseStudyBanner";
import Section from "../Section/Section";
import { Trans, useTranslation } from "react-i18next";
import { TitleHighlight } from "components/Section/Section.styled";
import useDevice from "hooks/useDevice";
import { PageSections } from "./CaseStudyTemplate.styled";

import { ColorModeContext } from "context/ColorModeContext";
import PropTypes from "prop-types";
import CaseStudyStats from "components/DataComponents/CaseStudyStats/CaseStudyStats";
import SubSection from "components/SubSection/SubSection";
import TextWithList from "components/TextWithList/TextWithList";
import CareersSelectionProcess from "components/DataComponents/CareersSelectionProcess/CareersSelectionProcess";
import YourIdeasSection from "components/DataComponents/YourIdeasSection/YourIdeasSection";
import StoryCards from "components/DataComponents/StoryCards/StoryCards";
import CareersSelectionProcessCardCricleImage from "components/DataComponents/CareersSelectionProcess/CareersSelectionProcessCardCricleImage/CareersSelectionProcessCardCricleImage";
import ClientCard from "components/Cards/ClientCard/ClientCard";
import { caseStudies } from "constants/caseStudies";
import { getRandomElements } from "util/helpers/randomData";
import { useLocation } from "react-router-dom";

const CaseStudyTemplatePage = ({ children, data }) => {
  const { isMobile, isTablet } = useDevice();
  const { t } = useTranslation();
  const { theme } = useContext(ColorModeContext);
  const { pathname } = useLocation();

  const moreStories1 = getRandomElements(caseStudies, [pathname]);
  const moreStories = moreStories1.map((item) => {
    return {
      title: item.name ?? item.header,
      link: item.link,
      badges: [item.category, item.industry, item.location],
      highlightColor: item.isInHouseProject
        ? theme.colors.thertiary
        : undefined,
    };
  });

  return (
    <PageSections>
      <Section>
        <CaseStudyBanner
          header={data.banner.header}
          text={data.banner.text}
          image={data.banner.image}
          badges={data.banner.badges}
          highlightColor={data.banner.highlightColor}
          imageHeight={data.banner.imageHeight}
          imageObjectFit={data.banner.imageObjectFit}
        />
      </Section>
      <Section
        margin={isMobile ? "64px 0px 24px" : "120px 0 80px"}
        paddingBottom={isMobile ? "10px" : "20px"}
      >
        <CaseStudyStats data={data.stats} />
      </Section>
      <Section
        isStyled={true}
        cardTitle={
          <Trans
            t={t}
            components={[<TitleHighlight isStyled={true} key={0} />]}
            i18nKey={data.description.header}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        {data.description.pargarafs.map((item, index) => (
          <SubSection
            key={index}
            image={item.image}
            text={item.text}
            reverse={index % 2 === 0}
            textColor={"dark"}
            height={isMobile ? "288px" : "480px"}
            width={isMobile ? "100%" : "420px"}
          />
        ))}
      </Section>
      <Section
        cardTitle={
          <Trans
            t={t}
            i18nKey={data.businessChallenges.header}
            components={[<TitleHighlight isStyled={true} key={0} />]}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        <TextWithList text={data.businessChallenges.text} textAlignRight />
      </Section>
      <Section
        cardTitle={
          <Trans
            t={t}
            i18nKey={data.ourSolutions.header}
            components={[<TitleHighlight isStyled={true} key={0} />]}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        <TextWithList text={data.ourSolutions.text} />
      </Section>
      <Section
        cardTitle={
          <Trans
            t={t}
            i18nKey={data.theResults.header}
            components={[<TitleHighlight isStyled={true} key={0} />]}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        {!(isMobile || isTablet) &&
          data.theResults.content?.map?.((item1, index) => (
            <CareersSelectionProcess
              key={index}
              imageText={item1.imageText}
              image={item1.image}
              header={item1.header}
              bgColor={item1.bgColor}
              IsEvenRow={index % 2 == 0}
            ></CareersSelectionProcess>
          ))}
        {(isMobile || isTablet) &&
          data.theResults.content?.map?.((item1, index) => (
            <CareersSelectionProcessCardCricleImage
              key={index}
              imageText={item1.imageText}
              image={item1.image}
              header={item1.header}
              bgColor={item1.bgColor}
              IsEvenRow={index % 2 == 0}
            ></CareersSelectionProcessCardCricleImage>
          ))}
      </Section>
      {children}
      {data.sthForOur && (
        <Section
          cardTitle={
            <Trans
              t={t}
              i18nKey={data.sthForOur.header}
              components={[<TitleHighlight isStyled={true} key={0} />]}
            />
          }
          margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
          paddingBottom={isMobile ? "10px" : "32px"}
        >
          <ClientCard
            width={"70%"}
            height={isMobile ? "auto" : "300px"}
            {...data.sthForOur.client}
          ></ClientCard>
        </Section>
      )}
      <Section
        cardTitle={
          <Trans
            t={t}
            i18nKey="ourWorkPage.yourIdeas.title"
            components={[<TitleHighlight isStyled={true} key={0} />]}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "160px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        <YourIdeasSection></YourIdeasSection>
      </Section>
      <Section
        cardTitle={
          <Trans
            t={t}
            i18nKey={data.moreStories.title}
            components={[<TitleHighlight isStyled={true} key={0} />]}
          />
        }
        margin={isMobile ? "64px 0px 6px" : "120px 0px 80px"}
        paddingBottom={isMobile ? "10px" : "32px"}
      >
        <StoryCards data={moreStories}></StoryCards>
      </Section>
    </PageSections>
  );
};

CaseStudyTemplatePage.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object,
};

CaseStudyTemplatePage.defaultProps = {};
export default CaseStudyTemplatePage;
